import React from 'react';
import { useSchemeLedger, useSchemeLedgerSummary } from '../network';
import type { LedgerEntry } from '../network';
import { Card, CardContent } from '../../../components/ui/Card';
import { Money } from '../../../components/ui/Money';
import { Skeleton } from '../../../components/ui/Skeleton';

export function SchemeLedgerTab({ schemeId }: { schemeId: string }) {
  const { data: ledger, isLoading, isError } = useSchemeLedger(schemeId);
  const { data: summary, isLoading: summaryLoading } = useSchemeLedgerSummary(schemeId);

  const entries: LedgerEntry[] = Array.isArray(ledger)
    ? (ledger as unknown as LedgerEntry[])
    : Array.isArray(ledger?.entries)
      ? ledger.entries
      : [];

  if (isLoading) {
    return <Skeleton rows={8} />;
  }

  if (isError) {
    return (
      <Card className="border border-border shadow-sm">
        <CardContent className="p-6 text-center text-text-secondary bg-surface-1">
          Failed to load ledger data
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {summaryLoading && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="border border-border shadow-sm">
              <CardContent className="p-4">
                <Skeleton rows={2} />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!summaryLoading && summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border border-border shadow-sm">
            <CardContent className="p-4">
              <p className="text-sm text-text-secondary">Total Collected</p>
              <p className="font-mono font-medium text-text-primary">
                <Money paise={summary.totalCollectedPaise} />
              </p>
            </CardContent>
          </Card>
          <Card className="border border-border shadow-sm">
            <CardContent className="p-4">
              <p className="text-sm text-text-secondary">Total Disbursed</p>
              <p className="font-mono font-medium text-text-primary">
                <Money paise={summary.totalDisbursedPaise} />
              </p>
            </CardContent>
          </Card>
          <Card className="border border-border shadow-sm">
            <CardContent className="p-4">
              <p className="text-sm text-text-secondary">Total Dividends</p>
              <p className="font-mono font-medium text-text-primary">
                <Money paise={summary.totalDividendsPaise} />
              </p>
            </CardContent>
          </Card>
          <Card className="border border-border shadow-sm">
            <CardContent className="p-4">
              <p className="text-sm text-text-secondary">Commission Earned</p>
              <p className="font-mono font-medium text-text-primary">
                <Money paise={summary.commissionEarnedPaise} />
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      <Card className="border border-border shadow-sm overflow-hidden">
        <CardContent className="p-0">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-surface-2">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-text-secondary uppercase tracking-wider">Debit</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-text-secondary uppercase tracking-wider">Credit</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-text-secondary uppercase tracking-wider">Balance</th>
              </tr>
            </thead>
            <tbody className="bg-surface-1 divide-y divide-border">
              {entries.map((entry: LedgerEntry) => (
                <tr key={entry.id} className="hover:bg-surface-2 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-text-primary text-sm">
                    {entry.date}
                  </td>
                  <td className="px-6 py-4 text-text-primary text-sm">
                    {entry.description || '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-surface-2 text-text-secondary">
                      {entry.entryType || '—'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-text-primary font-mono text-sm">
                    {entry.debitPaise > 0 && <Money paise={entry.debitPaise} />}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-text-primary font-mono text-sm">
                    {entry.creditPaise > 0 && <Money paise={entry.creditPaise} />}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right font-mono text-sm">
                    <Money paise={entry.balancePaise} />
                  </td>
                </tr>
              ))}
              {!entries.length && (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-text-secondary bg-surface-1">
                    No ledger entries found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}